// ---------- les créations, gardées sur le téléphone et nulle part ailleurs ----------
// Jusqu'au 2026-10-30, une création partait sur le serveur (POST /me/creations) et s'y gardait,
// privée à son auteur. Certains joueurs y ont fait des puzzles de photos pornographiques : même
// codée, la grille EST l'image en basse définition, et le créateur ne veut pas de ce contenu dans
// sa base. Décision du même jour : la création ne quitte plus l'appareil, comme la photo dont elle
// vient. Le prix, accepté : elle ne suit pas le joueur sur un autre téléphone ni après une
// réinstallation, et l'écran d'enregistrement le dit.
//
// IndexedDB et non localStorage : une grille de 200 × 200 fait ~95 ko de JSON, vingt créations
// ~2 Mo, et le localStorage d'une WebView plafonne à ~5 M caractères qu'il partage déjà avec les
// sauvegardes de partie — il déborde chez les joueurs assidus (cf. makeRoom dans progress.ts).
// IndexedDB n'a pas ce plafond, et range un tableau tel quel, sans JSON.
//
// La PROGRESSION d'une création reste où est celle de toutes les feuilles, dans progress.ts : elle
// est petite, et la lire à part demanderait un second chemin dans tout le jeu. Seule la grille
// est ici. Ce que sync.ts n'envoie plus au serveur pour une création, cf. isCreation là-bas.

import type { PuzzleDetail, PuzzleSummary } from './api';

const DB_NAME = 'codigrid';
const STORE = 'creations';

/** Ce qu'une entrée garde : la carte (ce que « Mes créations » affiche) et la grille. */
interface Stored {
  id: string;
  summary: PuzzleSummary;
  target: number[][];
}

let opening: Promise<IDBDatabase> | null = null;

function db(): Promise<IDBDatabase> {
  return (opening ??= new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'id' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }).catch((err) => {
    // Un échec d'ouverture n'est pas mémorisé : le prochain appel retente.
    opening = null;
    throw err;
  }));
}

/** Une transaction sur le magasin, rendue en promesse — résolue quand elle est ÉCRITE (`oncomplete`),
 *  pas quand la requête est acceptée : c'est seulement là qu'une création est sûre. */
async function run<T>(
  mode: IDBTransactionMode,
  op: (s: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T | undefined> {
  const tx = (await db()).transaction(STORE, mode);
  const req = op(tx.objectStore(STORE));
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(req ? req.result : undefined);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/** Les créations de l'appareil, la plus récente d'abord — l'ordre que servait le serveur. */
export async function listCreations(): Promise<PuzzleSummary[]> {
  const all = ((await run('readonly', (s) => s.getAll())) ?? []) as Stored[];
  return all.map((e) => e.summary).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export async function getCreation(id: string): Promise<PuzzleDetail | null> {
  const e = (await run('readonly', (s) => s.get(id))) as Stored | undefined;
  if (!e) return null;
  const { title, alphabetId, gridW, gridH } = e.summary;
  return { id, title, alphabetId, gridW, gridH, target: e.target };
}

export async function hasCreation(id: string): Promise<boolean> {
  return !!(await run('readonly', (s) => s.getKey(id)));
}

export async function putCreation(summary: PuzzleSummary, target: number[][]): Promise<void> {
  await run('readwrite', (s) => s.put({ id: summary.id, summary, target } satisfies Stored));
}

export async function removeCreation(id: string): Promise<void> {
  await run('readwrite', (s) => s.delete(id));
}

/** « Effacer mes données » (cf. wipe dans prefs.ts) : les créations sont des données du joueur
 *  comme les autres, et elles n'existent plus qu'ici. */
export async function clearCreations(): Promise<void> {
  await run('readwrite', (s) => s.clear());
}
